const SIGNIFICANT_DIGITS = 12;

export const ratio = (numerator, denominator) => (denominator ? numerator / denominator : null);

/**
 * Compute relative real from theoretical ratio (100% when real but no theoretical).
 * @param {number|null} gapFromTheoretical - The gap between real and theoretical consumption
 * @param {number|null} theoreticalConsumption - The theoretical consumption value
 * @returns {number|null} Percentage (0-100+) or null
 */
export const relativeRealFromTheoreticalRatio = (gapFromTheoretical, theoreticalConsumption) => {
  const backupValue = gapFromTheoretical != null && gapFromTheoretical > 0 ? 100 : null;

  return theoreticalConsumption
    ? ratio(gapFromTheoretical, theoreticalConsumption) * 100
    : backupValue;
};

export const roundWithDecimals = (value, decimals = 2) =>
  Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);

/**
 * Checks whether two numbers are close enough for a given decimal precision.
 * The tolerance is one unit at the least significant decimal: 10^(-decimals).
 * For example, decimals=2 means the gap must be below 0.01, decimals=1 means below 0.1,
 * and decimals=0 means below 1.
 *
 * The comparison is done on the real gap (not on values rounded to the tolerance),
 * so a 0.009 gap stays within a 0.01 tolerance. The gap is rounded at a higher
 * precision to absorb floating point noise (e.g. 1.901 * 10 = 19.009999999999998
 * must count as an exact 0.01 gap).
 *
 * @param {number} firstNumber - The first number to compare.
 * @param {number} secondNumber - The second number to compare.
 * @param {number} [decimals=2] - Number of decimal places used to define the tolerance.
 * @returns {boolean} True when the absolute gap is strictly below the tolerance.
 */
export const areNumbersWithinDecimalTolerance = (firstNumber, secondNumber, decimals = 2) => {
  const factor = Math.pow(10, decimals);
  const gapInUnits = roundWithDecimals(Math.abs(firstNumber - secondNumber) * factor, 6);

  return gapInUnits < 1;
};

/**
 * Drop the noise carried by a computation (e.g. 29.01 * 1.6 = 46.416000000000004),
 * while keeping enough significant digits to preserve any legitimate precision.
 *
 * @param {number} value - The computed value to clean.
 * @returns {number} The value without its floating point noise.
 */
export const clearFloatingPointNoise = (value) =>
  Number.isFinite(value) ? Number(value.toPrecision(SIGNIFICANT_DIGITS)) : value;
